import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Minus } from "lucide-react";

const fmt = (n) => `$${Number(n).toFixed(2)}`;

export default function Cart() {
    const { items, remove, updateQty, subtotal, clear } = useCart();
    const [promo, setPromo] = useState("");
    const [appliedPromo, setAppliedPromo] = useState(null);
    const nav = useNavigate();

    const discount = appliedPromo
        ? Math.min(subtotal, subtotal * ((appliedPromo.discount_percent || 0) / 100) + (appliedPromo.discount_amount || 0))
        : 0;
    const total = Math.max(0, subtotal - discount);

    const applyPromo = async () => {
        if (!promo.trim()) return;
        try {
            const { data } = await api.get(`/promo/validate/${promo.trim()}`);
            setAppliedPromo(data);
            toast.success(`Promo "${data.code}" applied`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Invalid promo code");
            setAppliedPromo(null);
        }
    };

    if (items.length === 0) {
        return (
            <div data-testid="cart-empty" className="max-w-3xl mx-auto px-6 py-32 text-center">
                <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">Your Cart</div>
                <h1 className="font-display text-5xl mb-6">Empty for now.</h1>
                <p className="text-[#6B645C] mb-8">Discover our curated tint films and installation tools.</p>
                <Link to="/automotive-window-film" className="px-8 py-4 inline-block bg-gold text-white uppercase tracking-[0.2em] text-xs font-semibold hover:bg-gold-hover">Shop Window Film</Link>
            </div>
        );
    }

    return (
        <div data-testid="cart-page" className="max-w-7xl mx-auto px-6 md:px-12 py-16">
            <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">Your Cart</div>
            <h1 className="font-display text-5xl mb-12">Review & Checkout</h1>

            <div className="grid lg:grid-cols-[1.6fr_1fr] gap-12">
                <div className="space-y-3">
                    {items.map((it, i) => (
                        <div key={it.product_id} data-testid={`cart-item-${i}`} className="flex gap-4 bg-charcoal-800 border border-[#E2D4C1] p-4">
                            <div className="w-24 h-24 bg-[#E2D4C1] overflow-hidden flex-shrink-0">
                                {it.image && <img src={it.image} alt={it.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1">
                                <div className="font-display text-lg">{it.name}</div>
                                <div className="text-gold mt-1">{fmt(it.price)}</div>
                                <div className="flex items-center gap-3 mt-3">
                                    <button data-testid={`qty-dec-${i}`} onClick={() => updateQty(it.product_id, it.quantity - 1)} className="w-7 h-7 border border-[#E2D4C1] flex items-center justify-center hover:border-gold"><Minus size={12} /></button>
                                    <span className="text-sm w-6 text-center" data-testid={`qty-${i}`}>{it.quantity}</span>
                                    <button data-testid={`qty-inc-${i}`} onClick={() => updateQty(it.product_id, it.quantity + 1)} className="w-7 h-7 border border-[#E2D4C1] flex items-center justify-center hover:border-gold"><Plus size={12} /></button>
                                </div>
                            </div>
                            <div className="flex flex-col items-end justify-between">
                                <button data-testid={`remove-item-${i}`} onClick={() => remove(it.product_id)} className="text-[#6B645C] hover:text-red-400"><Trash2 size={16} /></button>
                                <div className="text-sm">{fmt(it.price * it.quantity)}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <aside className="bg-charcoal-800 border border-[#E2D4C1] p-8 self-start">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#6B645C] mb-4">Order Summary</div>
                    <div className="space-y-3 text-sm">
                        <div className="flex justify-between"><span className="text-[#33302C]">Subtotal</span><span data-testid="cart-subtotal">{fmt(subtotal)}</span></div>
                        {appliedPromo && <div className="flex justify-between text-gold-light"><span>Promo ({appliedPromo.code})</span><span data-testid="cart-discount">-{fmt(discount)}</span></div>}
                        <div className="border-t border-[#E2D4C1] pt-3 flex justify-between text-lg"><span>Total</span><span className="text-gold font-medium" data-testid="cart-total">{fmt(total)}</span></div>
                    </div>

                    <div className="mt-6">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6B645C] mb-2">Promo Code</div>
                        <div className="flex gap-2">
                            <Input data-testid="promo-input" aria-label="Promo code" value={promo} onChange={e => setPromo(e.target.value)} placeholder="Enter code" className="bg-[#E2D4C1] border-[#E2D4C1] rounded-none focus-visible:ring-gold" />
                            <Button data-testid="apply-promo" onClick={applyPromo} variant="outline" className="border-gold/40 text-gold rounded-none uppercase text-xs tracking-widest hover:bg-gold hover:text-white">Apply</Button>
                        </div>
                    </div>

                    <Button data-testid="checkout-btn" onClick={() => nav("/checkout", { state: { appliedPromo } })} className="w-full mt-6 bg-gold hover:bg-gold-hover text-white rounded-none uppercase tracking-[0.2em] text-xs font-semibold py-6">
                        Proceed to Checkout
                    </Button>
                    <button data-testid="clear-cart" onClick={clear} className="text-xs text-[#6B645C] hover:text-gold mt-4 w-full text-center uppercase tracking-widest">Clear Cart</button>
                </aside>
            </div>
        </div>
    );
}
